import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Contact.css";
import mountainImg from '../assets/image/mountain_village.png';

/* ── Inline SVG Icons (no extra library needed) ── */
const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.08 6.08l1.28-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LocationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=100064934790013", Icon: FacebookIcon },
  { label: "Instagram", href: "https://www.instagram.com/tiwiziassociation/", Icon: InstagramIcon },
  { label: "LinkedIn", href: "/", Icon: LinkedInIcon },
];

const INITIAL_FORM = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wasValidated, setWasValidated] = useState(false);
  const [modal, setModal] = useState({ show: false, title: "", message: "", type: "error" });

  const closeModal = () => setModal((m) => ({ ...m, show: false }));
  const notify = (type, title, message) => setModal({ show: true, type, title, message });

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWasValidated(true);

    // Validation des champs obligatoires
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/contact/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setSubmitted(true);
        setForm(INITIAL_FORM);
        setWasValidated(false);

        setTimeout(() => {
          setSubmitted(false);
        }, 4500);
      } else {
        let errorMsg = t('contact.errSend');
        try {
          const data = await res.json();
          if (data && typeof data === 'object') {
            const messages = Object.entries(data)
              .map(([key, val]) => `${key} : ${Array.isArray(val) ? val.join(', ') : val}`)
              .join('\n');
            if (messages) errorMsg = messages;
          }
        } catch (_) {}
        notify('error', `Erreur ${res.status}`, errorMsg);
      }
    } catch (err) {
      console.error(err);
      notify('error', 'Erreur serveur', t('contact.errServer') || 'Connexion au serveur impossible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">

      {/* ── MODAL ERREUR ── */}
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : modal.type === 'warning' ? 'warning' : 'error'}`}>
              {modal.type === 'success' ? (
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              )}
            </div>
            <h3>{modal.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section className="ap-hero" style={{ backgroundImage: `url(${mountainImg})` }}>
        <div className="ap-hero-overlay" />
        <div className="ap-hero-content">
          <span className="ap-hero-badge">{t('contact.badgeHero')}</span>
          <h1 className="ap-hero-title">
            {t('contact.titleHero1')}<span className="keyword">{t('contact.titleKeyword')}</span>
          </h1>
          <p className="ap-hero-sub">
            {t('contact.subHero')}
          </p>
        </div>
        <div className="ap-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="var(--background-solid)" d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z" />
          </svg>
        </div>
      </section>

      {/* ── FORM SECTION ── */}
      <section className="cp-form-section" aria-label="Formulaire de contact">
        <div className="cp-container">
          <div className="cp-form-wrap">
            <div className="cp-section-header">
              <h2 className="cp-section-header__title">{t('contact.formTitle1')}<span className="cp-accent">{t('contact.formTitleKeyword')}</span></h2>
              <p className="cp-section-header__sub">{t('contact.formSub')}</p>
            </div>
            {submitted ? (
              <div className="cp-success" role="alert" aria-live="polite">
                <div className="cp-success__icon" aria-hidden="true">✓</div>
                <h3>{t('contact.successTitle')}</h3>
                <p>{t('contact.successDesc')}</p>
              </div>
            ) : (
              <form className="cp-form" onSubmit={handleSubmit} noValidate
                aria-label="Formulaire de contact">

                <div className="cp-form__row">
                  <div className="cp-form__group">
                    <label htmlFor="cp-name">{t('contact.labelName')} <span style={{color:'#e53935'}}>*</span></label>
                    <input
                      id="cp-name"
                      name="name"
                      type="text"
                      placeholder={t('contact.phName')}
                      value={form.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      className={wasValidated && !form.name.trim() ? 'input-error' : ''}
                    />
                  </div>
                  <div className="cp-form__group">
                    <label htmlFor="cp-email">{t('contact.labelEmail')} <span style={{color:'#e53935'}}>*</span></label>
                    <input
                      id="cp-email"
                      name="email"
                      type="email"
                      placeholder={t('contact.phEmail')}
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className={wasValidated && !form.email.trim() ? 'input-error' : ''}
                    />
                  </div>
                </div>

                <div className="cp-form__group">
                  <label htmlFor="cp-subject">{t('contact.labelSubject')} <span style={{color:'#e53935'}}>*</span></label>
                  <input
                    id="cp-subject"
                    name="subject"
                    type="text"
                    placeholder={t('contact.phSubject')}
                    value={form.subject}
                    onChange={handleChange}
                    required
                    className={wasValidated && !form.subject.trim() ? 'input-error' : ''}
                  />
                </div>

                <div className="cp-form__group">
                  <label htmlFor="cp-message">{t('contact.labelMessage')} <span style={{color:'#e53935'}}>*</span></label>
                  <textarea
                    id="cp-message"
                    name="message"
                    rows={6}
                    placeholder={t('contact.phMessage')}
                    value={form.message}
                    onChange={handleChange}
                    required
                    className={wasValidated && !form.message.trim() ? 'input-error' : ''}
                  />
                </div>

                <button type="submit" className="cp-submit" disabled={loading}
                  aria-busy={loading}>
                  {loading ? (
                    <span className="cp-submit__spinner" aria-hidden="true" />
                  ) : (
                    <>
                      <span>{t('contact.btnSubmit')}</span>
                      <ArrowIcon />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── MAP + INFO ── */}
      <section className="cp-main" aria-label="Informations de contact et localisation">
        <div className="cp-container">

          {/* LEFT — contact info */}
          <aside className="cp-info" aria-label="Coordonnées de l'association">
            <div className="cp-section-label">
              <span className="cp-dot" aria-hidden="true" />
              <span>{t('contact.infoLabel')}</span>
            </div>

            <h2 className="cp-info__title">{t('contact.infoTitle1')}<span className="cp-accent">{t('contact.infoTitleKeyword')}</span></h2>
            <p className="cp-info__desc">
              {t('contact.infoDesc')}
            </p>

            {/* Info cards */}
            <ul className="cp-cards">
              <li className="cp-card">
                <div className="cp-card__icon" aria-hidden="true"><MailIcon /></div>
                <div className="cp-card__body">
                  <span className="cp-card__label">{t('contact.cardEmailLabel')}</span>
                  <a href="mailto:tiwizi1998@gmail.com" className="cp-card__value">
                    tiwizi1998@gmail.com
                  </a>
                </div>
              </li>

              <li className="cp-card">
                <div className="cp-card__icon" aria-hidden="true"><PhoneIcon /></div>
                <div className="cp-card__body">
                  <span className="cp-card__label">{t('contact.cardPhoneLabel')}</span>
                  <a href="tel:+212666419108" className="cp-card__value" dir="ltr">
                    &#x202A;+212 0666 41 91 08&#x202C;
                  </a>
                </div>
              </li>

              <li className="cp-card">
                <div className="cp-card__icon" aria-hidden="true"><LocationIcon /></div>
                <div className="cp-card__body">
                  <span className="cp-card__label">{t('contact.cardAddressLabel')}</span>
                  <span className="cp-card__value">
                    {t('contact.cardAddressValue1')}<br />{t('contact.cardAddressValue2')}
                  </span>
                </div>
              </li>
            </ul>

            {/* Social links */}
            <div className="cp-social">
              <span className="cp-social__label">{t('contact.followUs')}</span>
              <div className="cp-social__links" role="list">
                {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                  <a key={label} href={href} className="cp-social__pill"
                    aria-label={label} role="listitem"
                    target="_blank" rel="noopener noreferrer">
                    <Icon />
                    <span>{label}</span>
                  </a>
                ))}
              </div>
            </div>
          </aside>

          {/* RIGHT — map */}
          <aside className="cp-map" aria-label="Localisation de l'association">
            <div className="cp-map__inner">
              <div className="cp-section-label">
                <span className="cp-dot" aria-hidden="true" />
                <span>{t('contact.mapLabel')}</span>
              </div>
              <h2 className="cp-map__title">
                {t('contact.mapTitle1')}<span className="cp-accent">{t('contact.mapTitleKeyword')}</span>
              </h2>
              <div className="cp-map__frame">
                <iframe
                  title="Tiwizi – Asni, Maroc"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13722.255613477776!2d-7.9800!3d31.2460!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdafee4f2d86a0b7%3A0x5c4d3c30d8c0e7a0!2sAsni%2C%20Morocco!5e0!3m2!1sfr!2sma!4v1700000000000"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ── FOOTER ── */}

    </div>
  );
}
